import { Component, ChangeDetectionStrategy, Input } from '@angular/core';
import { WIZ_BOOLEAN } from '@wizeoo/akm-core-lib';
import { CellCheckedEvent, WizEventManager, WizIcon, WizPopUp, WizTranslationManager } from '@wizeoo/akm-component-lib';
import { Pivot } from '../widgets/pivot/pivot.component';
import { VisionStepPerimeterVO } from '../../vo/vision/visionstepperimeter.vo';
import { FeiVO } from '../../vo/workingdata/fei.vo';
import { AlkEvent } from '../../framework/events/alk.event';
import { ALK_EVENT_TYPE, WIDGET_ACTION_ORIGIN, WIDGET_KIND, WIDGET_STATE } from '../../model/enums';
import { IWidget } from '../../manager/widgets/i-widget';
import { FeiEngine, WizColDef } from '../../manager/workingdata/fei.engine';
import { BaselineEngine, ComputeResult, FeiKeyDef, FormulaCoreEngine, MeasMode, MeasureCoreEngine, WidgetCellRenderer, WidgetLine, WizConstants } from '@wizeoo/akm-compute-lib';
import { Measure, WidgetConfigurationItemObj, WidgetConfigurationObj } from '@wizeoo/akm-message-lib';
import { ListFieldGridDatas } from '../../manager/workingdata/listfield.engine';
import { UpdateEntry, UpdateRequest } from '../../command/core/sync/sync.service';

@Component({
  selector: 'tool-pivot-component',
  templateUrl: './tool-pivot.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
/** rendu pivot du sample de création de lignes */
export class ToolPivot extends Pivot {  
  
  // afficher (ou pas) l'entête pour configuration
  @Input() showHeader: boolean = true;
  // mode perimeter (VSP), include (INCEX) ou rendu LIST (LIST)
  @Input() toolMode: string = FeiEngine.TOOL_MODE_VSP;
  // affichage de la ligne de cumul
  @Input() groupIncludeFooter: boolean = false;

  // données à utiliser lors de la gestion du rendu de LIST
  @Input() listFieldGridDatas: ListFieldGridDatas = null;

  // forcer le mode de sélection (mesure de type FILTER)
  // selectionMode: string = 'none';

  private _selectionMode: string = 'none';
  public get selectionMode(): string {
    // console.log("get sel mode tool", this._selectionMode);
    return this._selectionMode;
  }
  @Input() public set selectionMode(value: string) {
    this._selectionMode = value? value: 'none';
    // console.log("set sel mode tool", this._selectionMode);
  }

  /**
   * @Override pour générer des lignes de type TOOL
   */
  public buildColumns(): void {
    this.columns = this.model.feiEngine.buildToolGridColumns(this);
  }

  /** contrôle la présence des champs vitaux sur le widget passé en params */
  public feedContextVital(kind: WIDGET_KIND, referenceContext: WidgetConfigurationObj): void {
    super.feedContextVital(kind, referenceContext);
    referenceContext.pm = true;

    if(referenceContext.r.length <= 0) {
      let idx: number = 1; // positions
      let confItem: WidgetConfigurationItemObj;
      this.fei.keys.forEach((fkd: FeiKeyDef) => {
        confItem = this.model.widgetEngine.newWcio(fkd.fullCode, idx);
        confItem.lbl = fkd.label;
        confItem.pin = fkd.pinned;
  
        if(fkd.isKey && !fkd.isFilterKey) {  
          this.model.widgetEngine.wcioColParams(confItem, true, 50, 200); // taille de col
          referenceContext.r.push(confItem);
        } else {
          this.model.widgetEngine.wcioColParams(confItem, false, 70, 70); // taille de col
          referenceContext.d.push(confItem);
        }
        idx++;
      });
    }
  }

  /** Handler sur le clic d'une colonne de type boolean en mode vsp */
  onCellChecked(params: CellCheckedEvent) {
    
    // interception pour le mode VSP. Inutile en mode LIST
    // console.log('checkMe Im famous', params);
    if(this.toolMode === FeiEngine.TOOL_MODE_VSP) {
      if (params.newValue === WIZ_BOOLEAN.UNKNOWN) {
        // forcer le passage en 2 états, le undefined est juste un état d'affichage en pivot
        params.newValue = WIZ_BOOLEAN.FALSE;
      }
  
      const wizColDef: WizColDef = params.colDef as any as WizColDef;
      const widgetLine: WidgetLine = params.data;
      const widget: IWidget = wizColDef.widget;
      const fei: FeiVO = widget.fei as FeiVO;
      const vsp: VisionStepPerimeterVO = fei.visionStepPerimeterVO;
      
      widgetLine.ch?.forEach(child => {
        if (params.newValue === WIZ_BOOLEAN.TRUE) {
          this.model.visionEngine.addVspKey(child.key, vsp);
        } else { 
          this.model.visionEngine.deleteVspKey(child.key, vsp);
        }   
      });
    
      WizEventManager.dispatchEvent(this, new AlkEvent(ALK_EVENT_TYPE.VSP_SELECT_CHANGED, this));
      this.agGrid.api.refreshCells();
    } else {
      super.onCellChecked(params);
    }

  }

  /**
   * value Accessor en mode stepPerimeters
   */
  private vspValueAccessor(params: any): any {
    let result: string = WIZ_BOOLEAN.FALSE;
 
    const wizColDef: WizColDef = params.colDef as any as WizColDef;
    const widgetLine: WidgetLine = params.data;
    const widget: IWidget = wizColDef.widget;
    const fei: FeiVO = widget.fei as FeiVO;
    const vsp: VisionStepPerimeterVO = fei.visionStepPerimeterVO;
    
    let nbTrue: number = 0;
    let nbFalse: number = 0;
    let len: number = widgetLine.ch?.size || 0;
    [...widgetLine.ch?.values() || []].forEach(child => {
      if (vsp?.keyMap?.has(child.key)) { // on compte les true
        nbTrue++;
      } else {
        nbFalse++;
      }
    });

    if(nbTrue === len) {
      result = WIZ_BOOLEAN.TRUE
    } else if(nbFalse === len) {
      result = WIZ_BOOLEAN.FALSE
    } else {
      result = WIZ_BOOLEAN.UNKNOWN
    }

    return result; // Number(result);
  }

  /**
   * méthode d'accès générique sur les baselineValues.valor, depuis la WidgetLine
   * @param params la conf d'une cellule de grid. .data contient une WidgetLine
   */
  public valueAccessor(params: any): any {
    if (this.toolMode === FeiEngine.TOOL_MODE_VSP) {
      return this.vspValueAccessor(params);
    }
    else if (this.toolMode === FeiEngine.TOOL_MODE_LIST) {
      let res: any = super.valueAccessor(params);
      if(res === WIZ_BOOLEAN.UNKNOWN || !res || res === undefined) {
        res = WIZ_BOOLEAN.FALSE;
      }
      // console.log('tool valueAccessor', res, params);
      return res;
    }
  }

  /** fin de traitement d'update, contrôle d'intégrité avant envoi
    * @param updateRequest porteuse des widgetLines sources
    * @param updates porteur des demandes de modifs, pour rollback éventuel 
    */
  public postControlBeforeCommit(updateRequest: UpdateRequest, updates: UpdateEntry[]): void {
    let readyForCommit: boolean = true; // en cas de mesure, on contrôle la cohérence

    // console.log('tpc postControlBeforeCommit', updateRequest, this.mainDS);

    // contrôle des triggers
    // pas de trigger sur les tools this.checkTriggers(updates);

    readyForCommit = this.checkBeforeCommit(updateRequest, updates);

    if(readyForCommit) {
      // contrôle des lignes de cumul
      readyForCommit = this.listSumControl(updates);
    }

    if(readyForCommit) { // on commit pas vraiment, on mets juste à jour les fakes
      updates.forEach((ue: UpdateEntry) => {
        if(ue.newValue && Number(ue.newValue) !== 0) {
          this.listFieldGridDatas.valueMap.set(ue.baseline.key, ue.newValue);
        } else {
          this.listFieldGridDatas.valueMap.delete(ue.baseline.key);
        }
        
        // BaselineEngine.baselineSetValue(wl.bl, field, ue.newValue);
        this.model.cellMatrixEngine.addCell(ue.baseline.level, ue.baseline.key, ue.baseline.scen, ue.field, ue.newValue);
      });

    } else {
      // console.log('do rollback');
      updates.forEach((update: UpdateEntry) => {
        this.model.dataEngine.rollBackUpdate(update);
      });
    }

    // console.log('ready for commit', readyForCommit, updates);
    this.agGrid.api.refreshCells();
  }



  /**
   * construction pour rendu en mode liste
   * va alimenter la conf depuis un listFieldGridDatas
   */
  public feedForList(datas: ListFieldGridDatas): void {
    
    this.listFieldGridDatas = datas;

    const fei: FeiVO = this.listFieldGridDatas.renderFEI;

    this.state = WIDGET_STATE.READY;
    this.vision = fei.vision;
    this.visionStep = fei.visionStep;
    this.fei = fei; // on byPass le setDatasource
    this.context = datas.context;

    this.mainDS.setFilter(this.filterFunc.bind(this));

    this.alterContent(WIDGET_ACTION_ORIGIN.READY); // on déclenche le cycle de vie
  }

  /** forcer le déclenchement du filtre depuis l'extérieur */
  private currentFilter: string = null;
  public forceFilter(value: string): void {
    // console.log('forceFilter', value);
    this.currentFilter = value;
    this.mainDS.refresh();
  }

  // filterFunc de la collection de fdv
  public filterFunc(item: WidgetLine): boolean {
    // console.log('filter', item, this.currentFilter);
    // on va balayer la map des renderers
    // si c'est une ligne de consolidation, on balaie les ch
    let compareString = this.currentFilter?.toUpperCase();
    if(!compareString || compareString.length === 0) {
      return true;
    }

    let show: boolean = false; // pas visible par défaut
    
    // on check l'item
    this.forceRendererGenesis(item);
    [...item.rendererMap?.values() || []].forEach((wcr: WidgetCellRenderer) => {
      if(wcr.value?.toUpperCase().indexOf(compareString) >= 0) {
        show = true;
        return; // on sort de la boucle, je suis visible
      }
    });

    // deuxième chance pour les lignes de cumul, un enfant vivant
    if(item.isSubTotalLine && !show) { 
      [...item.ch?.values() || []].forEach(subItem => {
        this.forceRendererGenesis(subItem);
        [...subItem.rendererMap?.values() || []].forEach((wcr: WidgetCellRenderer) => {
          if(wcr.value?.toUpperCase().indexOf(compareString) >= 0) {
            show = true;
            return; // on sort de la boucle, je suis visible
          }
        });
        if(show) {
          return;
        }
      });
    }
 
    return show;
  }

  /**
   * forcer la génération des rendus des clés pour filtrer globalement
   * @param item widgetLine
   */
  private forceRendererGenesis(item: WidgetLine): void {
    if(!item.rendererMap) { // construction de la map des rendus
        
      this.agGrid?.columnApi.getAllColumns().forEach((col:any) => {
        if(col.colDef.fkd?.isKey) { // on prend les clés uniquement
          let raw = BaselineEngine.getLabel(this.fei, item, col.colDef.field, col.colDef.fdvfield); // prendre la bonne valeur de champ FD
          let result = this.model.formatEngine.applyFormat(raw, col.colDef.datakind, col.colDef.dataformat, null); // et formater
          this.setWidgetLineCellRendererValue(item, col.instanceId, raw || '', col.colDef.dataformat, result);
        }
        // console.log('columns', col);
      });
    }
  }

  /**
   * contrôle de cohérence sur les sous-totaux
   */
  public listControlOnSave(): void {
    // contrôle des sous-totaux
    if(this.listFieldGridDatas.subTotalControl) { // contrôle de cohérence demandé
      
      this.mainDS.getSource().forEach(l => { // on cherche les lignes de sous-total
        if(l.isSubTotalLine) {

          let fkd = this.fei.keyMap.get(this.listFieldGridDatas.measure.shortCode + "#I");
          let subValue: string = this.model.aggregateEngine.call(this.listFieldGridDatas.measure.defaultAgg, this.fei, [...l.ch.values()], fkd); 
          
          // fausse mesure & calcul
          let mesCode: string = subValue + this.listFieldGridDatas.subTotalControl.valueControl;
          let mm: MeasMode = MeasureCoreEngine.LightFakeMeasureMode(
                          this.fei,
                          [WizConstants.FAKE_INTEGRITY, 'subontrol'].join(WizConstants.CODE_LEVEL_SEPARATOR),
                          WizConstants.MEASURE_MODE_SUBMIT, 
                          '=IF((' + mesCode+ ');1;0)');
                          
          const cr: ComputeResult = FormulaCoreEngine.computeMmiDependencies(mm.mmi, l, this.fei, true);
            
          if(cr.valor !== '1') {
            let msg =  this.model.localeEngine.translate(this.listFieldGridDatas.subTotalControl.valueControlMessage, '$.common.error.$');
            WizPopUp.toast(msg, WizIcon.TOAST_ERROR).show();
          }
        }
      }); 
    }
    
  }

  /**
   * contrôle de cohérence sur les cumuls en mode list
   */
  private listSumControl(updates: UpdateEntry[]): boolean {
    let result: boolean = true; // ok si pas d'erreur
    let errorMsg: string = null; // msg d'erreur pour dispatch unique

    updates.forEach((ue: UpdateEntry) => {
      let fkd: FeiKeyDef = this.fei.keyMap.get(ue.field);
      let meas: Measure = fkd.measure;
      if(meas.maxValue) { // contrôle valeur max pour chaque ligne
        // console.log('yo max', meas);
        this.mainDS.getWorkedSource()?.forEach((wl: WidgetLine) => {
          if(wl.isSubTotalLine) {        
            // le pool de lignes
            let childPool: WidgetLine[] = [...wl.ch.values()];
            let aggMethod: string = meas.defaultAgg;
            let tmpresult = this.model.aggregateEngine.call(aggMethod, this.fei, childPool, fkd); 
            // console.log('subTot', wl, updateRequest, updates, tmpresult);
            if(tmpresult.length > 0 && Number(tmpresult) > Number(meas.maxValue)) {
              errorMsg = WizTranslationManager.getInstance().translateKey('validators.numberValidator.greaterThanMaxError') + ': ' + meas.maxValue;
              result = false;
            }
          }
        });
      }
    });

    if(!result) {
      WizPopUp.toast(errorMsg, WizIcon.TOAST_ERROR).show();
    }
    return result;
  }



}
